import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../../ui/resizable";
import { ScrollArea } from "../../ui/scroll-area";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { MousePointerClick, Filter, Globe, FolderOpen, ExternalLink, Layers, Power, Ban } from "lucide-react";
import { useAuth } from '../../contexts/AuthContext';
import config from '../../utils/config';
import { ShortLinkDetail } from "./ShortLinkDetail";
import { UrlMapping, getMyUrls, Workspace, getWorkspaces } from "../../utils/sqrone-api";
import { toast } from 'sonner';
import Branding from "../domains/Branding";
import { Workspaces } from "../workspaces/Workspaces";


export function ShortLinks() {
  const { token, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [urls, setUrls] = useState<UrlMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedUrlId, setSelectedUrlId] = useState<number | null>(null);
  const selectedUrl = urls.find((url) => url.id === selectedUrlId);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedActivationStatus, setSelectedActivationStatus] = useState<string>("all"); // "all", "active", "inactive"

  // FR-26: Initialize activeWorkspace from URL params if present
  const workspaceParam = searchParams.get('workspace');
  const [activeWorkspace, setActiveWorkspace] = useState<string>(workspaceParam || "all"); // Navigation state, not filter
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  // Get active workspace object
  const activeWorkspaceData = useMemo(() => {
    if (activeWorkspace === "all" || activeWorkspace === "none") return null;
    return workspaces.find(c => c.id === parseInt(activeWorkspace));
  }, [activeWorkspace, workspaces]);

  const [showWorkspaces, setShowWorkspaces] = useState(false);
  const [showDomains, setShowDomains] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);


  useEffect(() => {
    if (isAuthenticated) {
      fetchUrls();
      fetchWorkspaces();
    }
  }, [isAuthenticated]);

  // FR-26: Update activeWorkspace when URL params change
  useEffect(() => {
    const workspaceParam = searchParams.get('workspace');
    if (workspaceParam) {
      setActiveWorkspace(workspaceParam);
      // Clear the URL param after setting the workspace
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchUrls = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        toast.error('You must be logged in to view URLs');
        return;
      }

      const data = await getMyUrls(token);
      setUrls(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkspaces = async () => {
    try {
      if (!token) return;

      const data = await getWorkspaces(token);
      setWorkspaces(data);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
      // Don't show error to user, workspaces are optional
    }
  };

  // FR-18: Get domain from URL, preferring the domain field over extracting from shortUrl
  const getDomain = (url: UrlMapping): string => {
    // Use domain field if available (FR-13)
    if (url.domain) {
      return url.domain;
    }
    // Extract from shortUrl for legacy URLs
    if (url.shortUrl) {
      try {
        const urlObj = new URL(url.shortUrl);
        return urlObj.host; // Returns "example.com:8080" or "example.com"
      } catch {
        return config.apiBaseUrl.replace('http://', '').replace('https://', '');
      }
    }
    return config.apiBaseUrl.replace('http://', '').replace('https://', '');
  };

   // Get all unique tags from URLs
   const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    urls.forEach((url) => {
      url.tags?.split(',').forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [urls]);

  // Get all unique domains from URLs
  const allDomains = useMemo(() => {
    const domainSet = new Set<string>();
    urls.forEach((url) => {
      domainSet.add(getDomain(url));
    });
    return Array.from(domainSet).sort();
  }, [urls]);

  // Filter URLs based on active workspace (navigation) and filters (tags, domains, activation status)
  const filteredUrls = useMemo(() => {
    let filtered = urls;

    // Navigate to workspace (not a filter, but a container)
    if (activeWorkspace !== "all") {
      if (activeWorkspace === "none") {
        // Show URLs with no workspace
        filtered = filtered.filter((url) => !url.workspaceId);
      } else {
        // Show URLs from specific workspace
        const workspaceId = parseInt(activeWorkspace);
        filtered = filtered.filter((url) => url.workspaceId === workspaceId);
      }
    }

    // Apply filters within the current collection
    if (selectedTags.length > 0) {
      filtered = filtered.filter((url) =>
        selectedTags.some((tag) => url.tags?.split(",").includes(tag))
      );
    }

    if (selectedDomains.length > 0) {
      filtered = filtered.filter((url) =>
        selectedDomains.includes(getDomain(url))
      );
    }

    // FR-22: Filter by activation status
    if (selectedActivationStatus !== "all") {
      filtered = filtered.filter((url) =>
        selectedActivationStatus === "active" ? url.isActive !== false : url.isActive === false
      );
    }

    return filtered;
  }, [urls, selectedTags, selectedDomains, selectedActivationStatus, activeWorkspace]);

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const toggleDomainFilter = (domain: string) => {
    setSelectedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSelectedDomains([]);
    setSelectedActivationStatus("all");
    // Don't clear activeWorkspace - it's navigation, not a filter
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleUpdate = (updatedUrl: UrlMapping) => {
    // FR-19: Update the URL in the list with the new data (match by ID to handle shortCode updates)
    setUrls(urls.map(url =>
      url.id === updatedUrl.id ? updatedUrl : url
    ));
  };

  const handleCreate = (newUrl: UrlMapping) => {
    // Add the new URL to the list
    setUrls([newUrl, ...urls]);
    setIsCreatingNew(false);
    setSelectedUrlId(newUrl.id);
    setShowMobileDetail(true);
  };

  const handleNewLinkClick = () => {
    setIsCreatingNew(true);
    setSelectedUrlId(null);
    setShowMobileDetail(true);
  };

  const handleUrlSelect = (urlId: number) => {
    setSelectedUrlId(urlId);
    setShowMobileDetail(true);
  };

  const handleBackToList = () => {
    setShowMobileDetail(false);
    setIsCreatingNew(false);
  };

  const handleDelete = async (shortCode: string) => {
    if (!window.confirm('Are you sure you want to delete this shortened URL?')) {
      return;
    }

    try {
      setError(null);

      const response = await fetch(`${config.apiBaseUrl}/api/url/${shortCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete URL');
      }

      // Remove the URL from the list
      setUrls(urls.filter(url => url.shortCode !== shortCode));

      // Go back to list on mobile after deletion
      setShowMobileDetail(false);
      setSelectedUrlId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while deleting');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="shortlinks">
        <div className="auth-required">
          <p>Please sign in to manage your Checkpoints.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="shortlinks">
        <div className="loading">Loading your links...</div>
      </div>
    );
  }

  if (showWorkspaces) {
    return <Workspaces
      embedded={true}
      onBack={() => {
        setShowWorkspaces(false);
        fetchWorkspaces(); // Refresh workspaces when coming back
      }}
      onViewLinks={(workspaceId) => {
        // FR-26: Navigate to the selected workspace
        setActiveWorkspace(workspaceId.toString());
        setShowWorkspaces(false);
      }}
    />;
  }

  if (showDomains) {
    return <Branding embedded={true} onBack={() => setShowDomains(false)} />;
  }

  return (
    <div className="h-screen w-screen">
      <ResizablePanelGroup direction="horizontal" className="h-full">
        {/* Left Panel - URL List */}
        <ResizablePanel defaultSize={30} minSize={20} maxSize={50} className={showMobileDetail ? "hidden md:block" : ""}>
          <div className="h-full bg-muted/30 flex flex-col overflow-hidden">
            <div className="p-4 border-b space-y-3">
              {/* Workspace Navigation */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="size-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Workspaces</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowWorkspaces(true)}
                    className="h-6 text-xs"
                  >
                    Manage
                  </Button>
                </div>
                <div className="w-full overflow-x-auto overflow-y-hidden">
                  <div className="flex gap-2 pb-2 min-w-min">
                    <Button
                      size="sm"
                      variant={activeWorkspace === "all" ? "default" : "outline"}
                      onClick={() => setActiveWorkspace("all")}
                      className="whitespace-nowrap flex-shrink-0"
                    >
                      <Layers className="size-3 mr-1" />
                      All Checkpoints
                    </Button>
                    <Button
                      size="sm"
                      variant={activeWorkspace === "none" ? "default" : "outline"}
                      onClick={() => setActiveWorkspace("none")}
                      className="whitespace-nowrap flex-shrink-0"
                    >
                      Uncategorized
                    </Button>
                    {workspaces.map((workspace) => (
                      <Button
                        key={workspace.id}
                        size="sm"
                        variant={activeWorkspace === workspace.id.toString() ? "default" : "outline"}
                        onClick={() => setActiveWorkspace(workspace.id.toString())}
                        className="whitespace-nowrap flex-shrink-0"
                      >
                        <FolderOpen className="size-3 mr-1" />
                        {workspace.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Workspace Context Header */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">
                    {activeWorkspace === "all"
                      ? "All Checkpoints"
                      : activeWorkspace === "none"
                      ? "Uncategorized Links"
                      : activeWorkspaceData?.name || "Workspace"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {filteredUrls.length} {selectedTags.length > 0 || selectedDomains.length > 0 ? "filtered" : ""} link{filteredUrls.length !== 1 ? "s" : ""}
                    {activeWorkspaceData?.description && (
                      <span className="ml-1">• {activeWorkspaceData.description}</span>
                    )}
                  </p>
                </div>
                {/* Action Buttons  */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDomains(true)}
                    title="Manage Domains"
                  >
                    <Globe className="size-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFilterPanelOpen(!filterPanelOpen)}
                    className={selectedTags.length > 0 || selectedDomains.length > 0 || selectedActivationStatus !== "all" ? "border-primary" : ""}
                  >
                    <Filter className="size-4" />
                    {(selectedTags.length > 0 || selectedDomains.length > 0 || selectedActivationStatus !== "all") && (
                      <Badge variant="secondary" className="ml-1 size-5 rounded-full p-0 flex items-center justify-center text-xs">
                        {selectedTags.length + selectedDomains.length + (selectedActivationStatus !== "all" ? 1 : 0)}
                      </Badge>
                    )}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleNewLinkClick}
                    title="New Link"
                  >
                    <ExternalLink className="size-4" />
                  </Button>
                </div>
              </div>

              {/* Filter Panel */}
              <Collapsible open={filterPanelOpen} onOpenChange={setFilterPanelOpen}>
                <CollapsibleContent>
                  <div className="p-3 bg-background rounded-md border space-y-3">
                    {/* FR-22: Activation Status Filter */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Filter by Status</span>
                        {selectedActivationStatus !== "all" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedActivationStatus("all")}
                            className="h-6 text-xs px-2"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={selectedActivationStatus === "all" ? "default" : "outline"}
                          onClick={() => setSelectedActivationStatus("all")}
                          className="flex-1"
                        >
                          All
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedActivationStatus === "active" ? "default" : "outline"}
                          onClick={() => setSelectedActivationStatus("active")}
                          className="flex-1 gap-1"
                        >
                          <Power className="size-3" />
                          Active
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedActivationStatus === "inactive" ? "default" : "outline"}
                          onClick={() => setSelectedActivationStatus("inactive")}
                          className="flex-1 gap-1"
                        >
                          <Ban className="size-3" />
                          Inactive
                        </Button>
                      </div>
                    </div>

                    {/* Domain Filter */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Filter by Domain</span>
                        {selectedDomains.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedDomains([])}
                            className="h-6 text-xs px-2"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {allDomains.length > 0 ? (
                          allDomains.map((domain) => (
                            <div
                              key={domain}
                              className="flex items-center gap-2 px-2 py-1 bg-muted rounded hover:bg-muted/80 cursor-pointer transition-colors"
                              onClick={() => toggleDomainFilter(domain)}
                            >
                              <Checkbox
                                checked={selectedDomains.includes(domain)}
                                onCheckedChange={() => toggleDomainFilter(domain)}
                              />
                              <span className="text-xs font-mono">{domain}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground">No domains available</p>
                        )}
                      </div>
                    </div>

                    {/* Tag Filter */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Filter by Tags</span>
                        {selectedTags.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedTags([])}
                            className="h-6 text-xs px-2"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {allTags.length > 0 ? (
                          allTags.map((tag) => (
                            <div
                              key={tag}
                              className="flex items-center gap-2 px-2 py-1 bg-muted rounded hover:bg-muted/80 cursor-pointer transition-colors"
                              onClick={() => toggleTagFilter(tag)}
                            >
                              <Checkbox
                                checked={selectedTags.includes(tag)}
                                onCheckedChange={() => toggleTagFilter(tag)}
                              />
                              <span className="text-xs">{tag}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground">No tags available</p>
                        )}
                      </div>
                    </div>

                    {/* Clear All */}
                    {(selectedTags.length > 0 || selectedDomains.length > 0 || selectedActivationStatus !== "all") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={clearFilters}
                        className="w-full"
                      >
                        Clear all filters
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-2">
                {filteredUrls.length > 0 ? (
                  filteredUrls.map((url) => (
                  <div
                    key={url.id}
                    onClick={() => handleUrlSelect(url.id)}
                    className={`
                      p-3 mb-1 rounded-lg cursor-pointer transition-colors
                      ${
                        selectedUrlId === url.id
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50"
                      }
                    `}
                  >
                    {/* FS7: Name as primary visual reference */}
                    <div className="font-semibold text-sm mb-1 truncate">
                      {url.name || <span className="text-muted-foreground italic">Unnamed checkpoint</span>}
                    </div>
                    {/* Short URL as secondary reference */}
                    <div className="text-xs text-muted-foreground mb-2 font-mono truncate">
                      {getDomain(url)}/{url.shortCode}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* FR-22: Activation Status Badge */}
                      <Badge
                        variant={url.isActive !== false ? "default" : "destructive"}
                        className="text-xs flex items-center gap-1"
                      >
                        {url.isActive !== false ? (
                          <>
                            <Power className="size-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <Ban className="size-3" />
                            Inactive
                          </>
                        )}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        <MousePointerClick className="size-3" />
                        {url.clicks.toLocaleString()}
                      </Badge>
                      {/* Show workspace badge only when viewing "All Checkpoints" */}
                      {activeWorkspace === "all" && url.workspaceId && workspaces.find(c => c.id === url.workspaceId) && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <FolderOpen className="size-3" />
                          {workspaces.find(c => c.id === url.workspaceId)?.name}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">{url.createdAt}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <Filter className="size-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">No URLs found</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    No URLs match the selected filters
                  </p>
                  <Button size="sm" variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
              </div>    
              )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle className="hidden md:flex" />

        {/* Right Panel - URL Details */}
        <ResizablePanel defaultSize={70} className={!showMobileDetail ? "hidden md:block" : ""}>
          <ShortLinkDetail
            shortLink={selectedUrl}
            workspace={activeWorkspaceData}
            onCopy={handleCopy}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onCreate={handleCreate}
            mode={isCreatingNew ? 'create' : 'view'}
            onCancelCreate={() => setIsCreatingNew(false)}
            onBack={handleBackToList}
            isMobileView={showMobileDetail}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
